import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { useNavigate } from "react-router";
import { Button, Input, Form, Spin } from "antd";
import { EyeOutlined, EyeInvisibleOutlined, MailOutlined, LockOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { setChangeTestProd } from "@/store/reducers/testProdSelect";

// API imports
import {
  user_login_log,
  session_login_api,
  get_user_details,
  get_employee_access_list,
  get_list_of_setting,
  get_oidc_start_api,
  session_sso_complete_api,
  markJustLoggedIn,
} from '@/utils/api';
import Axios from 'axios';

// Store imports
import { setforgotPasswordShow } from '@/store/reducers/forgotPassword';
import { setOtpShow } from '@/store/reducers/otpValueShow';
import { setLoginShow } from '@/store/reducers/loginShow';
import { LOGIN_AUTH, LOGOUT_AUTH } from '@/store/reducers/auth';

// Component imports
import AnimateButton from '@/components/@extended/AnimateButton';
import { defaultColors } from '@/themes/antd-theme';

// ============================|| AUTH LOGIN FORM ||============================ //

// ============================|| SSO ||============================ //
// "Sign in with SSO" goes through the central auth proxy (Zitadel). The proxy redirects back here
// with ?login=success and its own session cookie; session_sso_complete_api exchanges that for a
// normal BMR session, after which the app loads exactly as after a password login.

const SSO_PICK_ACCOUNT = 'sso_pick_account';
const SSO_ERROR_KEY = 'sso_login_error';

const AuthLogin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [lockInfo, setLockInfo] = React.useState(null); // { type, message, attemptsRemaining, lockedUntil }
  const [ssoLoading, setSsoLoading] = React.useState(false);
  // Returning from the auth proxy: cover the form from the first paint until the session exchange
  // finishes, so the login page never shows before the main page.
  const [returningFromSso, setReturningFromSso] = React.useState(
    () => new URLSearchParams(window.location.search || '').get('login') === 'success'
  );

  React.useEffect(() => {
    // An SSO refusal is raised on the instance rendered during the return from the proxy, and the
    // navigate('/login') that follows mounts a fresh one - the message is carried across here.
    try {
      const carried = sessionStorage.getItem(SSO_ERROR_KEY);
      if (carried) {
        sessionStorage.removeItem(SSO_ERROR_KEY);
        setLockInfo(JSON.parse(carried));
      }
    } catch (_e) { /* storage unavailable: the message is simply not shown */ }
    const params = new URLSearchParams(window.location.search || '');
    const login = params.get('login');
    const ssoError = params.get('error');
    if (!login && !ssoError) return;
    window.history.replaceState({}, document.title, window.location.pathname + (window.location.hash || ''));
    if (ssoError) {
      setLockInfo({ type: 'error', message: ssoError });
      return;
    }
    if (login === 'success') completeSso();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on return from the auth proxy
  }, []);

  const startSso = () => {
    setLockInfo(null);
    setSsoLoading(true);
    get_oidc_start_api((res) => {
      const url = res?.authorize_url || res?.data?.authorize_url;
      if (res?.status === 'success' && url) {
        // After a refused SSO login Zitadel would silently reuse the same account, so ask it to
        // show the account chooser once.
        const pick = sessionStorage.getItem(SSO_PICK_ACCOUNT) === '1';
        sessionStorage.removeItem(SSO_PICK_ACCOUNT);
        window.location.href = pick ? `${url}${url.includes('?') ? '&' : '?'}prompt=select_account` : url;
        return;
      }
      setSsoLoading(false);
      setLockInfo({ type: 'error', message: res?.message || 'Single sign-on is not available.' });
    });
  };

  const completeSso = () => {
    setSsoLoading(true);
    session_sso_complete_api(async ({ ok, data }) => {
      try {
        if (ok && data.status === 'success') {
          await bootstrapSession();
          navigate('/mbmr-dash');
        } else {
          if (data.error === 'sso_user_not_provisioned' || data.error === 'account_inactive') {
            sessionStorage.setItem(SSO_PICK_ACCOUNT, '1');
          }
          const failure = { type: data.error === 'account_inactive' ? 'inactive' : 'error',
            message: data.message || 'Single sign-on failed.' };
          setLockInfo(failure);
          try { sessionStorage.setItem(SSO_ERROR_KEY, JSON.stringify(failure)); } catch (_e) { /* ignore */ }
          setReturningFromSso(false);
          dispatch(LOGOUT_AUTH()); // a stale persisted login must not bounce us into the app
          navigate('/login', { replace: true }); // we may be on the app's front URL
        }
      } catch (error) {
        setReturningFromSso(false);
        console.error('Error during SSO login:', error);
        toast.error('Something went wrong during login.');
      } finally {
        setSsoLoading(false);
      }
    });
  };

  const user_login_log_data = async (username, password) => {
    await user_login_log({ username, password }, (res) => {
      if (res?.data?.status === "success") {
        console.log(res?.data?.message);
      }
    });
  };

  const success_get_employee_access_list = (res, shouldNavigate = false) => {
    localStorage.setItem("user_access_data", JSON.stringify(res));

    // Only navigate if explicitly requested (after all data is loaded)
    if (shouldNavigate) {
      navigate("/mbmr-dash");
    }
  };

  const success_get_spp_setting = (res) => {
    if (res?.data?.status === "success") {
      localStorage.setItem("app_setting", JSON.stringify(res?.data?.data));
    }
  };

  const success_get_user_details = async (details) => {
    localStorage.setItem("user_details", JSON.stringify(details[0]));
  };

  const setDefaultPlant = async () => {
    try {
      // Check if plant is already set in localStorage
      const existingPlant = localStorage.getItem('active-plant');
      if (existingPlant && existingPlant !== 'null' && existingPlant !== 'undefined') {
        console.log('Plant already set, skipping default plant selection');
        return;
      }

      // Fetch user plants — authenticated by the session cookie (withCredentials).
      const response = await Axios.get('/bp/plants/user');

      if (response?.data?.status === 'success') {
        const { plants: userPlants } = response?.data?.data;

        // Set first plant as default if available
        if (userPlants && userPlants.length > 0) {
          const firstPlant = userPlants[0];
          console.log('Setting first plant as default on login:', firstPlant);
          localStorage.setItem('active-plant', JSON.stringify(firstPlant));
        }
      }
    } catch (error) {
      console.error('Error setting default plant:', error);
    }
  };

  // Shared by password and SSO login once the server has set the HttpOnly session cookie.
  const bootstrapSession = async () => {
    // Session-cookie login — the HttpOnly `sessionid` cookie is set by the
    // server; no token to store. Subsequent requests authenticate via the
    // cookie (Axios withCredentials is on). Mark the auth mode so the
    // route guard uses cookie/session logic instead of token-expiry.
    localStorage.setItem("auth_mode", "session");
    // 2026-10-12 · every login starts on Trial — Production is a deliberate switch in the header,
    // never inherited from the previous person's (or session's) choice. A refresh keeps it.
    dispatch(setChangeTestProd("test"));
    // Start a short grace window so any transient 401 from the bootstrap
    // calls below doesn't wrongly trigger the "session expired" redirect.
    markJustLoggedIn();

    // Each bootstrap call resolves even on error (the API helpers only
    // call `success` on 2xx and swallow errors in .catch), plus a safety
    // timeout — so a single slow/failed call can never leave login hung
    // on this page. Login still succeeds; missing data reloads later.
    const settle = (executor, ms = 8000) =>
      new Promise((resolve) => {
        let done = false;
        const finish = (v) => { if (!done) { done = true; resolve(v); } };
        try { executor(finish); } catch (_e) { finish(); }
        setTimeout(finish, ms);
      });

    const loadUserDetails = settle((resolve) => {
      get_user_details((details) => {
        success_get_user_details(details);
        resolve();
      });
    });
    const loadEmployeeAccess = settle((resolve) => {
      get_employee_access_list((res) => {
        success_get_employee_access_list(res, false);
        resolve(res);
      });
    });
    const loadSettings = settle((resolve) => {
      get_list_of_setting((res) => {
        success_get_spp_setting(res);
        resolve();
      });
    });
    const loadDefaultPlant = setDefaultPlant();

    await Promise.all([loadUserDetails, loadEmployeeAccess, loadSettings, loadDefaultPlant]);
    dispatch(LOGIN_AUTH());
  };

  const user_login = async (username, password) => {
    setLoading(true);
    setLockInfo(null);
    try {
      await session_login_api(username, password, async ({ ok, status, data }) => {
        try {
          if (ok && data.status === "success") {
            await bootstrapSession();
            await user_login_log_data(username, password);
            navigate("/mbmr-dash");
          } else if (status === 423 || data.error === 'account_locked') {
            setLockInfo({
              type: 'locked',
              lockedSince: data.locked_since,
              message: data.message || 'Account locked due to too many failed attempts. Contact admin to unlock.',
            });
          } else if (status === 403 || data.error === 'account_inactive') {
            // Account deactivated by admin
            setLockInfo({
              type: 'inactive',
              message: data.message || 'Your account has been deactivated. Please contact an administrator.',
            });
          } else {
            // Wrong credentials — show attempts remaining if provided
            const remaining = data.attempts_remaining;
            if (remaining !== undefined && remaining !== null) {
              setLockInfo({ type: 'warning', attemptsRemaining: remaining });
            } else {
              setLockInfo({ type: 'error', message: data.message || data.detail || 'Invalid credentials.' });
            }
          }
        } catch (error) {
          console.error("Error during login process:", error);
          toast.error("Something went wrong during login.");
        } finally {
          setLoading(false);
        }
      });
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Login failed. Please try again.");
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    setLockInfo(null);
    try {
      localStorage.setItem("username", values.email);
      await user_login(values.email, values.password);
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error("An error occurred. Please try again.");
      setLoading(false);
    }
  };

  if (returningFromSso) {
    return (
      <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-white">
        <Spin size="large" tip="Signing you in…"><div className="p-12" /></Spin>
      </div>
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      autoComplete="off"
      className="w-full"
      initialValues={{
        email: "",
        password: "",
      }}
    >
      {/* User ID Field */}
      <Form.Item
        label={<span className="text-sm font-medium">User ID</span>}
        name="email"
        rules={[
          {
            required: true,
            message: 'Please enter your user ID',
          },
        ]}
      >
        <Input
          prefix={<MailOutlined className="text-gray-400" />}
          placeholder="Enter Username"
          size="large"
          className="rounded-md"
        />
      </Form.Item>

      {/* Password Field */}
      <Form.Item
        label={<span className="text-sm font-medium">Password</span>}
        name="password"
        rules={[
          {
            required: true,
            message: 'Password is required',
          },
        ]}
      >
        <Input.Password
          prefix={<LockOutlined className="text-gray-400" />}
          placeholder="Enter password"
          size="large"
          className="rounded-md"
          iconRender={(visible) =>
            visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
          }
        />
      </Form.Item>

      {/* Forgot Password Link */}
      <div className="flex justify-end mb-4 -mt-2">
        <RouterLink
          to=""
          onClick={(e) => {
            e.preventDefault();
            dispatch(setforgotPasswordShow(true));
            dispatch(setOtpShow(false));
            dispatch(setLoginShow(false));
          }}
          className="text-sm no-underline hover:underline"
          style={{ color: defaultColors.primary }}
        >
          Forgot Password?
        </RouterLink>
      </div>

      {/* Lock / error alerts */}
      {lockInfo && (
        <div className="mb-4 rounded-lg overflow-hidden" style={{
          border: lockInfo.type === 'warning'
            ? '1px solid #f59e0b'
            : '1px solid #ef4444',
          background: lockInfo.type === 'warning'
            ? 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)'
            : 'linear-gradient(135deg, #fff5f5 0%, #fee2e2 100%)',
        }}>
          {/* coloured top bar */}
          <div style={{
            height: 3,
            background: lockInfo.type === 'warning'
              ? 'linear-gradient(90deg, #f59e0b, #fbbf24)'
              : 'linear-gradient(90deg, #ef4444, #dc2626)',
          }} />

          <div className="flex items-start gap-3 px-4 py-3">
            {/* icon circle */}
            <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5" style={{
              background: lockInfo.type === 'warning' ? '#fef3c7' : '#fee2e2',
              border: lockInfo.type === 'warning' ? '1.5px solid #f59e0b' : '1.5px solid #ef4444',
            }}>
              {lockInfo.type === 'locked' && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              )}
              {lockInfo.type === 'inactive' && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="17" y1="8" x2="23" y2="14"/><line x1="23" y1="8" x2="17" y2="14"/>
                </svg>
              )}
              {lockInfo.type === 'warning' && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              )}
              {lockInfo.type === 'error' && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold mb-0.5" style={{
                color: lockInfo.type === 'warning' ? '#92400e' : '#991b1b',
              }}>
                {lockInfo.type === 'locked' && 'Account Locked'}
                {lockInfo.type === 'inactive' && 'Account Deactivated'}
                {lockInfo.type === 'warning' && 'Invalid Credentials'}
                {lockInfo.type === 'error' && 'Login Failed'}
              </p>
              <p className="text-xs leading-relaxed" style={{
                color: lockInfo.type === 'warning' ? '#b45309' : '#b91c1c',
              }}>
                {lockInfo.type === 'warning'
                  ? `${lockInfo.attemptsRemaining} attempt${lockInfo.attemptsRemaining === 1 ? '' : 's'} remaining before your account is locked.`
                  : lockInfo.message}
              </p>

              {/* attempt dots for warning — backend default is 3 max wrong attempts */}
              {lockInfo.type === 'warning' && (
                <div className="flex gap-1 mt-2">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="w-2 h-2 rounded-full" style={{
                      background: i <= (3 - lockInfo.attemptsRemaining) ? '#ef4444' : '#d1d5db',
                    }} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <Form.Item className="mb-0">
        <AnimateButton>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={loading}
            className="w-full font-semibold"
            style={{
              backgroundColor: defaultColors.primary,
              borderColor: defaultColors.primary,
            }}
          >
            Login
          </Button>
        </AnimateButton>
      </Form.Item>

      <Form.Item className="mt-3 mb-0">
        <Button size="large" className="w-full font-semibold" loading={ssoLoading} onClick={startSso}>
          Sign in with SSO
        </Button>
      </Form.Item>
    </Form>
  );
};

export default AuthLogin;
